import { TabsContent, FormField } from '@/components/molecules';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Input,
  Button,
} from '@/components/atoms';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState, useMemo, useEffect } from 'react';
import { Camera } from 'lucide-react';
import {
  useUser,
  useUpdateUser,
  uploadFile,
  useRequestPhoneUpdate,
  useConfirmPhoneUpdate,
  useRequestEmailUpdate,
  useConfirmEmailUpdate,
} from '@/api';
import toast from 'react-hot-toast';
import { OTPConfirmationDialog } from '../otp-confirmation-dialog';
import {
  profileUpdateSchema,
  type ProfileUpdateFormData,
} from '@/validation-schema';

export const ProfileTab = () => {
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isOTPDialogOpen, setIsOTPDialogOpen] = useState(false);
  const [otpType, setOtpType] = useState<'phone' | 'email'>('phone');
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [isPhoneEditable, setIsPhoneEditable] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: user, refetch: refetchUser } = useUser('me');
  const { mutate: updateProfile, isPending } = useUpdateUser(
    user?.id?.toString() || 'me',
    () => {
      // Reset avatar file and refetch user data after successful update
      setAvatarFile(null);
      setRemoveAvatar(false);
      refetchUser();
    }
  );

  const { mutate: requestPhoneUpdate, isPending: isRequestingPhone } =
    useRequestPhoneUpdate();

  const { mutate: confirmPhoneUpdate, isPending: isConfirmingPhone } =
    useConfirmPhoneUpdate(() => {
      setIsOTPDialogOpen(false);
      setIsPhoneEditable(false);
      refetchUser();
    });

  const { mutate: requestEmailUpdate, isPending: isRequestingEmail } =
    useRequestEmailUpdate();

  const { mutate: confirmEmailUpdate, isPending: isConfirmingEmail } =
    useConfirmEmailUpdate(() => {
      setIsOTPDialogOpen(false);
      setIsEmailEditable(false);
      refetchUser();
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, dirtyFields },
    setValue,
    watch,
    getValues,
  } = useForm<ProfileUpdateFormData>({
    resolver: yupResolver(profileUpdateSchema),
    mode: 'onChange',
  });

  const hasAvatarChanged = !!avatarFile || removeAvatar;

  // const watchedEmail = watch('email');
  const watchedPhone = watch('whatsappNumber');

  useEffect(() => {
    if (user) {
      setValue('firstName', user.firstName || '');
      setValue('lastName', user.lastName || '');
      setValue('email', user.auth.email || '');
      setValue('whatsappNumber', user.whatsappNumber || '');
    }
  }, [user, setValue]);

  const avatarUrl = useMemo(() => {
    if (avatarFile) {
      return URL.createObjectURL(avatarFile);
    }
    if (removeAvatar) return undefined;
    return user?.avatar?.url;
  }, [avatarFile, removeAvatar, user?.avatar?.url]);

  const userInitials = useMemo(() => {
    if (!user?.firstName && !user?.lastName) return '';
    return `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  }, [user]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAvatarFile(file);
    if (file) setRemoveAvatar(false);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setRemoveAvatar(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (data: ProfileUpdateFormData) => {
    let avatarId: number | undefined;

    // Upload avatar if a new file is selected
    if (avatarFile) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('files', avatarFile);

        const res = await uploadFile(formData);
        avatarId = res.data[0]?.id;
      } catch (error) {
        console.error('Failed to upload avatar:', error);
        toast.error('Failed to upload avatar');
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    // Build payload with only dirty fields
    const payload: Record<string, unknown> = {};

    if (dirtyFields.firstName) payload.firstName = data.firstName;
    if (dirtyFields.lastName) payload.lastName = data.lastName;
    if (dirtyFields.whatsappNumber)
      payload.whatsappNumber = data.whatsappNumber;
    if (avatarId) payload.avatarId = avatarId;
    if (removeAvatar && !avatarId) payload.avatarId = null;

    // Only update if there are changes
    if (Object.keys(payload).length > 0) {
      updateProfile(payload);
    }
  };

  // const handleChangeEmail = () => {
  //   if (!isEmailEditable) {
  //     setIsEmailEditable(true);
  //     return;
  //   }
  //   const newEmail = getValues('email');
  //   if (newEmail && newEmail !== user?.auth.email) {
  //     setOtpType('email');
  //     setIsOTPDialogOpen(true);
  //   }
  // };

  const handleChangePhone = () => {
    if (!isPhoneEditable) {
      setIsPhoneEditable(true);
      return;
    }
    const newPhone = getValues('whatsappNumber');
    if (newPhone && newPhone !== user?.whatsappNumber) {
      setOtpType('phone');
      setIsOTPDialogOpen(true);
    }
  };

  const handleRequestOTP = () => {
    if (otpType === 'phone') {
      requestPhoneUpdate({ newPhone: getValues('whatsappNumber') });
    } else {
      requestEmailUpdate({ newEmail: getValues('email') });
    }
  };

  const handleConfirmOTP = (code: string) => {
    if (otpType === 'phone') {
      confirmPhoneUpdate({ newPhone: getValues('whatsappNumber'), code });
    } else {
      confirmEmailUpdate({ newEmail: getValues('email'), code });
    }
  };

  return (
    <TabsContent value="profile">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-6 max-w-xl mx-auto py-6"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <Avatar
              className="size-32 cursor-pointer"
              onClick={handleAvatarClick}
            >
              <AvatarImage
                alt="profile"
                className="object-cover rounded-full"
                src={avatarUrl}
              />
              <AvatarFallback className="text-4xl bg-primary/10 text-primary">
                {userInitials || 'U'}
              </AvatarFallback>
            </Avatar>
            <div
              className="absolute bottom-0 right-0 bg-primary rounded-full p-2 cursor-pointer"
              onClick={handleAvatarClick}
            >
              <Camera className="size-4 text-white" />
            </div>
          </div>
          {(!!user?.avatar?.url || !!avatarFile) && (
            <Button
              type="button"
              variant="outline"
              label="Remove photo"
              onClick={handleRemoveAvatar}
            />
          )}
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <FormField
          label="First Name"
          className="w-full"
          error={errors.firstName?.message}
        >
          <Input
            placeholder="Enter your first name"
            {...register('firstName')}
          />
        </FormField>

        <FormField
          label="Last Name"
          className="w-full"
          error={errors.lastName?.message}
        >
          <Input placeholder="Enter your last name" {...register('lastName')} />
        </FormField>

        <FormField
          label="Email Address"
          className="w-full"
          error={errors.email?.message}
        >
          <div className="flex gap-2 w-full">
            <Input
              type="email"
              placeholder="Enter your email"
              {...register('email')}
              disabled={!isEmailEditable}
              className="flex-1"
            />
            {/* <Button
              type="button"
              variant="outline"
              label={isEmailEditable ? 'Confirm' : 'Change'}
              onClick={handleChangeEmail}
              disabled={isEmailEditable && watchedEmail === user?.auth.email}
            /> */}
          </div>
        </FormField>

        <FormField
          label="Whatsapp Number"
          className="w-full"
          error={errors.whatsappNumber?.message}
        >
          <div className="flex gap-2 w-full">
            <Input
              placeholder="+234"
              {...register('whatsappNumber')}
              disabled={!isPhoneEditable}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              label={isPhoneEditable ? 'Confirm' : 'Change'}
              onClick={handleChangePhone}
              disabled={
                isPhoneEditable && watchedPhone === user?.whatsappNumber
              }
            />
          </div>
        </FormField>

        <Button
          className="w-full"
          label="Save"
          size="lg"
          type="submit"
          disabled={!hasAvatarChanged && (!isDirty || !isValid)}
          isLoading={isPending || isUploading}
        />
      </form>

      <OTPConfirmationDialog
        isOpen={isOTPDialogOpen}
        onClose={() => setIsOTPDialogOpen(false)}
        onConfirm={handleConfirmOTP}
        onSendOTP={handleRequestOTP}
        isLoading={
          isConfirmingPhone ||
          isConfirmingEmail ||
          isRequestingPhone ||
          isRequestingEmail
        }
        type={otpType}
        value={
          otpType === 'phone' ? getValues('whatsappNumber') : getValues('email')
        }
      />
    </TabsContent>
  );
};
